import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";
import { SERVICES } from "../data/clinic";
import { scrollToId } from "../lib/scroll";

const FeatureCard = ({ service, index }) => {
  const Icon = service.icon;
  return (
    <Reveal delay={index * 0.08}>
      <div
        data-testid={`service-card-${index}`}
        className="group relative h-full bg-sa-espresso rounded-[32px] p-8 sm:p-10 overflow-hidden"
      >
        <div className="absolute -top-16 -right-16 w-52 h-52 rounded-full bg-sa-bronze/20 blur-2xl group-hover:bg-sa-bronze/30 transition-colors duration-500" />
        <div className="relative z-10 flex flex-col h-full">
          <div className="w-14 h-14 rounded-2xl bg-sa-bronze/20 flex items-center justify-center mb-8">
            <Icon className="w-7 h-7 text-sa-bronze" />
          </div>
          <span className="font-body text-xs uppercase tracking-[0.2em] text-sa-bronze font-semibold">
            {service.badge}
          </span>
          <h3 className="font-display text-3xl sm:text-4xl text-sa-cream mt-3 mb-4">
            {service.title}
          </h3>
          <p className="font-body text-base text-sa-cream/70 leading-relaxed">
            {service.desc}
          </p>
          <button
            onClick={() => scrollToId("#booking")}
            data-testid={`service-book-${index}`}
            className="mt-8 inline-flex items-center gap-2 self-start rounded-full border border-sa-cream/25 px-5 py-2.5 font-body text-sm font-medium text-sa-cream hover:bg-sa-cream hover:text-sa-espresso transition-colors duration-300"
          >
            Book this
            <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </Reveal>
  );
};

const StandardCard = ({ service, index }) => {
  const Icon = service.icon;
  return (
    <Reveal delay={index * 0.05}>
      <div
        data-testid={`service-placeholder-${index}`}
        className="group h-full bg-sa-beige rounded-3xl p-7 border border-sa-espresso/5 hover:border-sa-bronze/40 hover:-translate-y-1 transition-[transform,border-color] duration-300"
      >
        <div className="w-11 h-11 rounded-xl bg-sa-cream flex items-center justify-center mb-5">
          <Icon className="w-5 h-5 text-sa-bronze" />
        </div>
        <h3 className="font-display text-xl text-sa-espresso mb-2">
          {service.title}
        </h3>
        <p className="font-body text-sm text-sa-mocha leading-relaxed">
          {service.desc}
        </p>
      </div>
    </Reveal>
  );
};

export const Services = () => {
  const featured = SERVICES.filter((s) => s.featured);
  const placeholders = SERVICES.filter((s) => !s.featured);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 sm:py-32 bg-sa-beige/50"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div className="max-w-xl">
            <Reveal>
              <SectionLabel testId="services-label">What we do</SectionLabel>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-sa-espresso mt-6">
                Everything your pet needs,
                <span className="italic text-sa-bronze"> under one roof</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <p className="font-body text-base text-sa-mocha max-w-sm leading-relaxed">
              From round-the-clock emergencies to routine wellness — full
              veterinary care for cats, dogs and birds, whenever you need it.
            </p>
          </Reveal>
        </div>

        {/* Featured bento */}
        <div className="grid md:grid-cols-2 gap-5 mb-6">
          {featured.map((service, i) => (
            <FeatureCard key={service.title} service={service} index={i} />
          ))}
        </div>

        {/* Standard services band */}
        <Reveal>
          <div className="flex items-center gap-3 mt-16 mb-8">
            <span className="h-px flex-1 bg-sa-espresso/15" />
            <span className="font-body text-xs uppercase tracking-[0.2em] text-sa-mocha font-semibold">
              More ways we care
            </span>
            <span className="h-px flex-1 bg-sa-espresso/15" />
          </div>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {placeholders.map((service, i) => (
            <StandardCard key={service.title} service={service} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
